import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  RefreshCw, Search, AlertTriangle, FileText, Clock, ChevronRight,
  PlayCircle, CheckCircle2, ArrowRightLeft, Flag, X,
  Mail, RotateCcw, Trash2, Calendar, Sparkles, Eye, Settings,
  XCircle, Loader, Inbox, BarChart3, SlidersHorizontal
} from 'lucide-react';
import { getEtlDlqList, getEtlDlqDetail, postEtlDlqAction, getEtlDlqGeneratePrompt, getEtlDlqArchiveFiles } from '../../utils/api';
import AiModelPicker from './AiModelPicker';
import ImportReviewModal from './ImportReviewModal';
import FileViewerPanel from './FileViewerPanel';
import FeedOptionsModal from './FeedOptionsModal';
import DlqCardList from './DlqCardList';
import DlqIssueSheet from './DlqIssueSheet';
import { detectFileViewerType } from '../../utils/fileViewer';
import { ToolbarButton, ConfigEmptyState } from '../ConfigPages';
import { RunStatePill, EtlTiles, EtlLoading, EtlBanner } from '../EtlPages';

// DlqSection — Phase 4 of the email-etl-rewrite plan.
// Renders the dead-letter-queue section of the FileTracking page. Reads
// per-file state machine rows from the etlDlq Azure Function (which queries
// etlFileProcessingState / etlFileProcessingEvents / etlDlqActions in
// production SQL).
//
// Layout: list view on the left, detail panel on the right when a row is
// selected. Filter chips at the top (status, fileType, error category, age).
// Action buttons in the detail panel are role-gated — CS users see Replay,
// Mark Resolved, Reassign, Escalate. Engineers see all of those plus Replay
// from earlier stage and Edit State.

// Customer-facing status labels. The raw values are pipeline state-machine
// statuses (ocrPending, normalizerPending, dlq…) — meaningless to hotel staff.
const STATUS_LABEL = {
  dlq: 'Needs Attention',
  failed: 'Failed',
  received: 'Received',
  ocrPending: 'Reading Document',
  ocrComplete: 'Document Read',
  normalizerPending: 'Processing',
  normalized: 'Processed',
  resolved: 'Resolved',
};

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'dlq', label: STATUS_LABEL.dlq, tone: 'danger' },
  { value: 'failed', label: STATUS_LABEL.failed, tone: 'warning' },
  { value: 'received', label: STATUS_LABEL.received, tone: 'info' },
  { value: 'ocrPending', label: STATUS_LABEL.ocrPending, tone: 'info' },
  { value: 'ocrComplete', label: STATUS_LABEL.ocrComplete, tone: 'info' },
  { value: 'normalizerPending', label: STATUS_LABEL.normalizerPending, tone: 'info' },
  { value: 'normalized', label: STATUS_LABEL.normalized, tone: 'success' },
  { value: 'resolved', label: STATUS_LABEL.resolved, tone: 'success' },
];

const CATEGORY_OPTIONS = [
  { value: '', label: 'All categories' },
  { value: 'DATE_MISMATCH', label: 'Date mismatch' },
  { value: 'AI_DATE_CORRECTION', label: 'AI date correction' },
  { value: 'JOURNAL_MISMATCH', label: 'Journal totals mismatch' },
  { value: 'WRONG_FILE_TYPE', label: 'Wrong file type' },
  { value: 'OCR_TIMEOUT', label: 'Document read timeout' },
  { value: 'OCR_QUALITY', label: 'Document quality issue' },
  { value: 'EXTRACTION_EMPTY', label: 'No data extracted' },
  { value: 'PARSE_ERROR', label: 'Extraction error' },
  { value: 'DATE_UNDETERMINED', label: 'Date undetermined' },
  { value: 'FILE_READ_ERROR', label: 'File read error' },
  { value: 'ARCHIVE_ERROR', label: 'Archive error' },
  { value: 'MULTI_FILE_ERROR', label: 'Multi-file error' },
  { value: 'DUPLICATE', label: 'Duplicate file' },
  { value: 'CONFIG_MISSING', label: 'Configuration missing' },
  { value: 'NORMALIZER_EXCEPTION', label: 'Processing error' },
  { value: 'SQL_CONSTRAINT_VIOLATION', label: 'Database conflict' },
  { value: 'ZERO_REVENUE_TRAP', label: 'Zero revenue detected' },
  { value: 'MISSED_FILE', label: 'File never received' },
  { value: 'UNKNOWN', label: 'Unknown' },
];

const DLQ_PAGE_SIZE = 250;
const RENDER_BATCH_SIZE = 200;

// Friendly label for a raw error category; falls back to un-shouting the enum.
function categoryLabel(value) {
  if (!value) return '—';
  const opt = CATEGORY_OPTIONS.find((o) => o.value === value);
  if (opt) return opt.label;
  const words = String(value).replace(/_/g, ' ').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Status pills come from the shared run-state vocabulary. This file used to
 * carry its own STATUS_TONE and STATUS_RAIL maps, which disagreed with both
 * FileTrackingShared and ConfigurationsSection — `resolved` was emerald here,
 * grey there and green in the third.
 */
function StatusPill({ status }) {
  return <RunStatePill status={status} label={STATUS_LABEL[status]} />;
}

function formatRelative(iso) {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '—';
  const diff = Date.now() - t;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function formatLocalWithRelative(iso) {
  if (!iso) return '—';
  const date = new Date(iso);
  if (!Number.isFinite(date.getTime())) return '—';
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  const local = sameDay
    ? date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    : date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  return `${local} (${formatRelative(iso)})`;
}

function formatAbsolute(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch (_) {
    return iso;
  }
}

function formatEffectiveDate(value) {
  if (!value) return '—';
  const dateOnly = value instanceof Date
    ? value.toISOString().slice(0, 10)
    : String(value).slice(0, 10);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateOnly);
  if (!match) return dateOnly;
  const localDate = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return localDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

// User role detection — mirrors the engineer/cs split that the backend uses
// in azfApi/etlDlq/index.js (getUserRole). The backend is the source of truth;
// the frontend uses this only to decide which buttons to render. Any
// engineer-only action sent without backend evidence is rejected with 403.
function detectRole(currentUser) {
  if (!currentUser) return 'cs';
  if (currentUser.isAdmin === true) return 'engineer';
  const orgAccess = currentUser.orgAccess || {};
  const isAdminInAnyOrg = Object.values(orgAccess).some(
    (oa) => oa && (oa.role === 'admin' || oa.role === 'owner')
  );
  return isAdminInAnyOrg ? 'engineer' : 'cs';
}

export default function DlqSection({ currentUser, currentOrganization, scopeOrgId, accessibleHotelIds, locations, dlqStats, onStatsRefresh, refreshTrigger, scopeReady = true }) {
  const [urlParams, setUrlParams] = useSearchParams();
  const role = useMemo(() => detectRole(currentUser), [currentUser]);

  // Build location name lookup from the locations prop
  const locationNameById = useMemo(() => {
    const map = new Map();
    for (const loc of (locations || [])) {
      const id = loc.locationId || loc.id;
      if (id) map.set(id, loc.name || loc.locationName || `Hotel ${id}`);
    }
    return map;
  }, [locations]);

  // Filters
  const initialStartDate = urlParams.get('from') || '';
  const initialEndDate = urlParams.get('to') || '';
  const initialLocationId = urlParams.get('locationId') || '';
  // 'all' was a legacy sentinel from the tile toggle; it used to reach the
  // server as a literal status and match nothing. Empty string means no filter.
  const [statusFilter, setStatusFilter] = useState(
    (() => { const v = urlParams.get('status') || ''; return v === 'all' ? '' : v; })()
  );
  const [fileTypeFilter, setFileTypeFilter] = useState(urlParams.get('fileType') || '');
  const [debouncedFileTypeFilter, setDebouncedFileTypeFilter] = useState(urlParams.get('fileType') || '');
  const [categoryFilter, setCategoryFilter] = useState(urlParams.get('category') || '');
  const [searchQuery, setSearchQuery] = useState(urlParams.get('q') || '');
  const [locationFilter, setLocationFilter] = useState(initialLocationId);
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [appliedScope, setAppliedScope] = useState(
    initialStartDate && initialEndDate
      ? { locationId: initialLocationId, startDate: initialStartDate, endDate: initialEndDate }
      : null
  );
  const [scopeError, setScopeError] = useState('');
  const loadRequestId = useRef(0);
  const [visibleRowCount, setVisibleRowCount] = useState(RENDER_BATCH_SIZE);
  const [isDesktop, setIsDesktop] = useState(() => (
    typeof window === 'undefined' || window.matchMedia('(min-width: 768px)').matches
  ));
  // Missed-file sentinels are hidden by default (they're surfaced in the
  // Historical view); this toggle brings them inline with real DLQ entries.
  const [includeMissed, setIncludeMissed] = useState(urlParams.get('missed') === 'true');
  // Client-side: keep only rows that carry an issue category or a last error.
  const [onlyWithErrors, setOnlyWithErrors] = useState(urlParams.get('errors') === 'true');
  // KPI tiles and the location/date-range scope block are collapsed by default
  // so the table gets the vertical space; the scope block auto-opens when a
  // range is already applied via URL so the user can see what's loaded.
  const [showTiles, setShowTiles] = useState(false);
  const [showScope, setShowScope] = useState(() => Boolean(urlParams.get('from') && urlParams.get('to')));

  // Data
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  // Phones: the selected issue opens in a bottom sheet; "Full details" swaps
  // to the full-screen pane for the long-form tools.
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
  const [mobileFullDetail, setMobileFullDetail] = useState(false);

  // Action modal state
  const [actionInProgress, setActionInProgress] = useState(false);
  const [actionFeedback, setActionFeedback] = useState(null);
  const [reassignType, setReassignType] = useState('');
  const [actionNotes, setActionNotes] = useState('');

  // AI Replay state
  const [aiReplayOpen, setAiReplayOpen] = useState(false);
  const [aiModel, setAiModel] = useState('sonnet');
  const [aiContext, setAiContext] = useState('');
  const [aiDataTypes, setAiDataTypes] = useState(['revenue', 'roomStats']);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiPromptLoading, setAiPromptLoading] = useState(false);

  // Import Review modal state
  const [importReviewOpen, setImportReviewOpen] = useState(false);

  // File Config edit modal state
  const [fileConfigEditOpen, setFileConfigEditOpen] = useState(false);

  // Archive files state
  const [archiveFiles, setArchiveFiles] = useState(null);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiveSelectedFile, setArchiveSelectedFile] = useState(null);

  // Reprocess Hotel Day modal state
  const [reprocessOpen, setReprocessOpen] = useState(false);
  const [reprocessHotelId, setReprocessHotelId] = useState('');
  const [reprocessStartDate, setReprocessStartDate] = useState('');
  const [reprocessEndDate, setReprocessEndDate] = useState('');
  const [reprocessIncludeArchive, setReprocessIncludeArchive] = useState(true);
  const [reprocessDryRun, setReprocessDryRun] = useState(false);
  const [reprocessInProgress, setReprocessInProgress] = useState(false);
  const [reprocessFeedback, setReprocessFeedback] = useState(null);

  /**
   * The organization to narrow to, or null for "all the ones I can see".
   *
   * This used to read the SIGNED-IN organization, so with the page scoped to
   * "All organizations" the queue still asked for one org — the header and the
   * list disagreed. `scopeOrgId` is the page's own selection (undefined when
   * the caller doesn't manage scope, in which case the old behaviour stands).
   */
  const orgId = scopeOrgId !== undefined
    ? scopeOrgId
    : (currentOrganization?.id || currentOrganization?.orgId || null);

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedFileTypeFilter(fileTypeFilter.trim()), 300);
    return () => clearTimeout(timeout);
  }, [fileTypeFilter]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setUrlParams((previous) => {
        const next = new URLSearchParams(previous);
        const setOrDelete = (key, value) => {
          if (value) next.set(key, value);
          else next.delete(key);
        };
        setOrDelete('locationId', appliedScope?.locationId);
        setOrDelete('from', appliedScope?.startDate);
        setOrDelete('to', appliedScope?.endDate);
        setOrDelete('status', statusFilter);
        setOrDelete('category', categoryFilter);
        setOrDelete('fileType', debouncedFileTypeFilter);
        setOrDelete('q', searchQuery.trim());
        if (includeMissed) next.set('missed', 'true');
        else next.delete('missed');
        if (onlyWithErrors) next.set('errors', 'true');
        else next.delete('errors');
        return next;
      }, { replace: true });
    }, 250);
    return () => clearTimeout(timeout);
  }, [appliedScope, statusFilter, categoryFilter, debouncedFileTypeFilter, searchQuery, includeMissed, onlyWithErrors, setUrlParams]);

  useEffect(() => {
    const media = window.matchMedia('(min-width: 768px)');
    const handleChange = (event) => setIsDesktop(event.matches);
    media.addEventListener('change', handleChange);
    return () => media.removeEventListener('change', handleChange);
  }, []);

  const sortedLocations = useMemo(() => (
    [...(locations || [])].sort((a, b) => {
      const aName = a.name || a.locationName || '';
      const bName = b.name || b.locationName || '';
      return aName.localeCompare(bName);
    })
  ), [locations]);

  const applyRange = () => {
    if (!startDate || !endDate) {
      setScopeError('Choose both a start and end date.');
      return;
    }
    if (startDate > endDate) {
      setScopeError('Start date must be on or before end date.');
      return;
    }
    setScopeError('');
    setAppliedScope({ locationId: locationFilter, startDate, endDate });
  };

  const clearRange = () => {
    setLocationFilter('');
    setStartDate('');
    setEndDate('');
    setScopeError('');
    setAppliedScope(null);
  };

  const activeFilterCount = [statusFilter, categoryFilter, fileTypeFilter.trim(), includeMissed, onlyWithErrors]
    .filter(Boolean).length;

  // Zeroed placeholder so the tile row can render before the stats arrive.
  const counts = dlqStats?.counts || {};

  const loadList = useCallback(async () => {
    // One request against the settled scope (see useEtlScope.scopeReady).
    if (!scopeReady) return;
    const requestId = ++loadRequestId.current;
    // Wait for the scope to resolve, but do not enumerate it: the server
    // derives the caller's accessible organizations and locations. `hotelIds`
    // is only sent to NARROW to one location the operator picked.
    if (!orgId && (!accessibleHotelIds || accessibleHotelIds.length === 0)) {
      setRows([]);
      setTotal(0);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const requestOptions = {
        hotelIds: appliedScope?.locationId ? [Number(appliedScope.locationId)] : undefined,
        status: statusFilter || undefined,
        fileType: debouncedFileTypeFilter || undefined,
        category: categoryFilter || undefined,
        includeMissed: includeMissed || (categoryFilter === 'MISSED_FILE') || undefined,
        startDate: appliedScope?.startDate,
        endDate: appliedScope?.endDate,
        limit: appliedScope ? DLQ_PAGE_SIZE : 100,
        offset: 0,
      };
      const firstPage = await getEtlDlqList(orgId, requestOptions);
      let allRows = firstPage?.rows || [];
      const matchingTotal = firstPage?.total || 0;

      // A scoped range is an explicit request for the complete result set.
      // Keep the API's defensive page cap and exhaust all pages client-side.
      if (appliedScope && allRows.length < matchingTotal) {
        const offsets = [];
        for (let offset = DLQ_PAGE_SIZE; offset < matchingTotal; offset += DLQ_PAGE_SIZE) {
          offsets.push(offset);
        }
        // Limit concurrency so a large range does not fan out dozens of
        // simultaneous Azure Function/SQL connections.
        for (let index = 0; index < offsets.length; index += 4) {
          const batch = offsets.slice(index, index + 4);
          const pages = await Promise.all(batch.map((offset) => (
            getEtlDlqList(orgId, { ...requestOptions, offset })
          )));
          if (loadRequestId.current !== requestId) return;
          allRows = allRows.concat(pages.flatMap((page) => page?.rows || []));
        }
      }

      if (loadRequestId.current !== requestId) return;
      setRows(allRows);
      setTotal(matchingTotal);
    } catch (err) {
      if (loadRequestId.current === requestId) {
        setError(err.message || 'Failed to load DLQ');
      }
    } finally {
      if (loadRequestId.current === requestId) setLoading(false);
    }
  }, [orgId, accessibleHotelIds, scopeReady, statusFilter, debouncedFileTypeFilter, categoryFilter, includeMissed, appliedScope]);

  useEffect(() => { loadList(); }, [loadList, refreshTrigger]);

  const loadDetail = useCallback(async (id) => {
    setDetailLoading(true);
    setDetail(null);
    try {
      const data = await getEtlDlqDetail(id);
      setDetail(data);
    } catch (err) {
      setActionFeedback({ tone: 'error', message: err.message || 'Failed to load detail' });
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const handleSelect = (id) => {
    setSelectedId(id);
    setMobileFullDetail(false);
    setMobileSheetOpen(true);
    setActionFeedback(null);
    setReassignType('');
    setActionNotes('');
    setAiReplayOpen(false);
    setAiPrompt('');
    setAiContext('');
    setImportReviewOpen(false);
    setFileConfigEditOpen(false);
    setArchiveFiles(null);
    setArchiveOpen(false);
    setArchiveSelectedFile(null);
    loadDetail(id);
  };

  const filteredRows = useMemo(() => {
    const base = onlyWithErrors
      ? rows.filter((r) => r.lastErrorCategory || (r.lastErrorMessage || '').trim())
      : rows;
    if (!searchQuery) return base;
    const q = searchQuery.toLowerCase();
    return base.filter((r) => {
      const locName = (locationNameById.get(r.locationId) || '').toLowerCase();
      return String(r.locationId || '').includes(q)
        || locName.includes(q)
        || (r.fileType || '').toLowerCase().includes(q)
        || String(r.effectiveDate || '').toLowerCase().includes(q)
        || (r.lastErrorMessage || '').toLowerCase().includes(q)
        || (r.sourceBlobPath || '').toLowerCase().includes(q);
    });
  }, [rows, searchQuery, locationNameById, onlyWithErrors]);

  useEffect(() => setVisibleRowCount(RENDER_BATCH_SIZE), [filteredRows]);

  const visibleRows = useMemo(
    () => filteredRows.slice(0, visibleRowCount),
    [filteredRows, visibleRowCount]
  );

  const handleRowsScroll = (event) => {
    const element = event.currentTarget;
    if (element.scrollHeight - element.scrollTop - element.clientHeight < 240
        && visibleRowCount < filteredRows.length) {
      setVisibleRowCount((count) => Math.min(count + RENDER_BATCH_SIZE, filteredRows.length));
    }
  };

  const performAction = async (action, extraPayload = {}) => {
    if (!selectedId) return;
    if ((action === 'mark_resolved' || action === 'escalate') && !actionNotes.trim()) {
      setActionFeedback({ tone: 'error', message: 'A note is required for this action.' });
      return;
    }
    if (action === 'reassign_type' && !reassignType.trim()) {
      setActionFeedback({ tone: 'error', message: 'New file type is required.' });
      return;
    }
    setActionInProgress(true);
    setActionFeedback(null);
    try {
      const result = await postEtlDlqAction(action, {
        stateId: selectedId,
        notes: actionNotes.trim() || null,
        performedByRole: role,
        ...extraPayload,
      });
      // Build a feedback message that surfaces what actually happened. For
      // rescue actions we want to show the count cleared / restored / triggered
      // so the operator can confirm at a glance.
      let message = `${action} completed`;
      if (action === 'undo_dedupe' && typeof result?.cleared === 'number') {
        message = `Cleared ${result.cleared} dedupe row(s) for messageId`;
      } else if (action === 'retry_ocr') {
        const restored = result?.restoreResult?.restored ? 'restored from archive' : 'used existing working/ files';
        const triggered = result?.triggerResult?.triggered ? 'triggered formRecognizerEtl' : 'trigger pending';
        message = `Retry OCR: ${restored}, ${triggered}`;
      } else if (action === 'replay_with_ai') {
        const model = result?.aiModel || 'AI';
        const triggered = result?.triggerResult?.triggered ? 'triggered normalizerEtl' : 'trigger pending';
        message = `AI Replay (${model}): ${triggered}`;
      } else if (action === 'convert_to_ai') {
        message = result?.converted
          ? `Converted ${result.fileType} to AI Extraction (${result.preferredModel})`
          : 'Conversion completed';
      } else if (result?.triggerResult?.triggered === false) {
        message = `${action} recorded; trigger pending`;
      }
      setActionFeedback({ tone: 'success', message });
      // Refresh both list and detail to reflect the new status.
      await loadList();
      await loadDetail(selectedId);
      if (onStatsRefresh) onStatsRefresh();
      setActionNotes('');
      setReassignType('');
    } catch (err) {
      const msg = err?.response?.data?.error || err.message || 'Action failed';
      setActionFeedback({ tone: 'error', message: msg });
    } finally {
      setActionInProgress(false);
    }
  };

  const submitReprocessHotelDay = async () => {
    if (!reprocessHotelId.trim() || !reprocessStartDate.trim()) {
      setReprocessFeedback({ tone: 'error', message: 'Hotel ID and start date are required.' });
      return;
    }
    setReprocessInProgress(true);
    setReprocessFeedback(null);
    try {
      const result = await postEtlDlqAction('reprocess_hotel_day', {
        hotelId: reprocessHotelId.trim(),
        startDate: reprocessStartDate.trim(),
        endDate: reprocessEndDate.trim() || null,
        includeArchive: reprocessIncludeArchive,
        dryRun: reprocessDryRun,
        performedByRole: role,
      });
      const cleared = typeof result?.dedupeCleared === 'number' ? result.dedupeCleared : '?';
      const triggered = result?.reprocessResult?.triggered ? 'triggered emailReprocess' : 'trigger pending';
      setReprocessFeedback({
        tone: 'success',
        message: `Cleared ${cleared} dedupe row(s); ${triggered}`,
      });
      await loadList();
    } catch (err) {
      const msg = err?.response?.data?.error || err.message || 'Reprocess failed';
      setReprocessFeedback({ tone: 'error', message: msg });
    } finally {
      setReprocessInProgress(false);
    }
  };

  // On phones the issue opens in a bottom sheet over the list unless the
  // operator asked for the full-screen detail pane.
  const mobileSheetMode = !isDesktop && !mobileFullDetail;
  const showDetailPane = Boolean(selectedId) && !mobileSheetMode;
  const closeMobileSheet = () => {
    setMobileSheetOpen(false);
    setSelectedId(null);
    setDetail(null);
  };
  // Radix makes the page inert while the sheet is open, so close it (keeping
  // the selection) before opening one of this section's own modals.
  const fromSheet = (fn) => () => { setMobileSheetOpen(false); fn(); };

  // Bound to the viewport so the list and the detail/timeline pane each
  // scroll on their own instead of stretching the page (ConfigPageShell's
  // .cp-page has no height of its own). Offset ≈ shell head + tabs.
  return (
    <div className="flex min-h-0 overflow-hidden gap-3" style={{ height: 'calc(100vh - 200px)', minHeight: 480 }}>
      {/* Left: list + filters — hidden on mobile when detail is open */}
      <div className={`flex-1 flex flex-col min-w-0 bg-white rounded-xl md:rounded-2xl border border-gray-200 overflow-hidden ${showDetailPane ? 'hidden md:flex' : ''}`}>
        <div className="cp-toolbar">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <h2 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--lodge-onyx-900)' }}>Processing issues</h2>
            <span className="cp-meta">{total.toLocaleString()}</span>
          </div>
          <div className="cp-toolbar-spacer" />
          <ToolbarButton
            variant="ghost" active={showTiles} small
            icon={BarChart3}
            onClick={() => setShowTiles((v) => !v)}
            aria-pressed={showTiles}
            title={showTiles ? 'Hide summary tiles' : 'Show summary tiles'}
          >
            Summary
          </ToolbarButton>
          <ToolbarButton
            variant="ghost" active={showScope || Boolean(appliedScope)} small
            icon={SlidersHorizontal}
            onClick={() => setShowScope((v) => !v)}
            aria-pressed={showScope}
            title={showScope ? 'Hide location and date range' : 'Show location and date range'}
          >
            {appliedScope ? `Range · ${appliedScope.startDate} → ${appliedScope.endDate}` : 'Range'}
          </ToolbarButton>
          {role === 'engineer' && (
            <ToolbarButton icon={Mail} onClick={() => setReprocessOpen(true)}>
              Reprocess hotel day
            </ToolbarButton>
          )}
          <ToolbarButton iconOnly icon={RefreshCw} onClick={loadList} disabled={loading} aria-label="Refresh" title="Refresh" />
        </div>

        {/* Always rendered, zeroed until the stats land. Mounting this row only
            once the response arrived pushed the filters and the table down mid-
            load, which read as the page re-rendering itself. */}
        {showTiles ? (
        <div style={{ padding: '12px 14px 0' }}>
          <EtlTiles
            tiles={[
              { key: 'dlq', label: 'Needs attention', n: counts.dlq || 0, tone: 'error', icon: AlertTriangle },
              { key: 'failed', label: 'Failed', n: counts.failed || 0, tone: 'error', icon: XCircle },
              { key: 'stale', label: 'Stale over 24h', n: counts.staleOver24h || 0, tone: 'warn', icon: Clock },
              { key: 'stuckOcr', label: 'Stuck reading', n: counts.stuckOcr || 0, tone: 'warn', icon: Loader },
              {
                key: 'inflight',
                label: 'In flight',
                tone: 'info',
                icon: Inbox,
                n: (counts.received || 0) + (counts.ocrPending || 0)
                  + (counts.ocrComplete || 0) + (counts.normalizerPending || 0),
              },
            ]}
            value={statusFilter || null}
            onChange={(key) => setStatusFilter(key && ['dlq', 'failed'].includes(key) ? key : '')}
          />
        </div>
        ) : null}

        {/* Scope and filters. The business-date range is a deliberate second
            step: without it the endpoint returns only the newest page, and
            silently showing a truncated queue is how a stuck file gets missed. */}
        <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--lodge-onyx-100)', display: 'grid', gap: 8 }}>
          {showScope ? (
          <div className="ep-panel" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 10 }}>
            <div style={{ flex: '1 1 200px', minWidth: 0 }}>
              <label className="cp-label" htmlFor="dlq-loc">Location</label>
              <select
                id="dlq-loc"
                className="cp-input"
                style={{ width: '100%' }}
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
              >
                <option value="">All accessible locations</option>
                {sortedLocations.map((loc) => {
                  const id = loc.locationId || loc.id;
                  return <option key={id} value={id}>{loc.name || loc.locationName || `Hotel ${id}`}</option>;
                })}
              </select>
            </div>
            <div style={{ flex: '0 1 150px' }}>
              <label className="cp-label" htmlFor="dlq-from">Business date from</label>
              <input id="dlq-from" type="date" className="cp-input" style={{ width: '100%' }}
                     value={startDate} max={endDate || undefined} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div style={{ flex: '0 1 150px' }}>
              <label className="cp-label" htmlFor="dlq-to">through</label>
              <input id="dlq-to" type="date" className="cp-input" style={{ width: '100%' }}
                     value={endDate} min={startDate || undefined} onChange={(e) => setEndDate(e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {appliedScope && <ToolbarButton variant="ghost" onClick={clearRange}>Clear</ToolbarButton>}
              <ToolbarButton variant="primary" icon={Calendar} onClick={applyRange} disabled={loading || !startDate || !endDate}>
                Load range
              </ToolbarButton>
            </div>
          </div>
          ) : null}

          {scopeError ? <EtlBanner tone="error">{scopeError}</EtlBanner> : null}
          {appliedScope && !loading && !error ? (
            <div className="cp-help">
              Loaded all {rows.length.toLocaleString()} matching records from {appliedScope.startDate} through {appliedScope.endDate}.
            </div>
          ) : null}
          {!appliedScope && !loading && !error && rows.length < total ? (
            <div className="cp-help" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <AlertTriangle size={13} style={{ color: 'var(--color-warn-ink, #b45309)' }} />
              <span>
                Showing the newest {rows.length.toLocaleString()} of {total.toLocaleString()} records.{' '}
                <button type="button" onClick={() => setShowScope(true)} style={{ background: 'none', border: 0, padding: 0, font: 'inherit', color: 'var(--lodge-orange-600, #c2410c)', textDecoration: 'underline', cursor: 'pointer' }}>
                  Load a business-date range
                </button>{' '}
                to see every match.
              </span>
            </div>
          ) : null}

          <div className="cp-toolbar ep-dlq-filters" style={{ padding: 0, border: 'none', background: 'transparent' }}>
            <div className="cp-search">
              <Search size={14} />
              <input
                type="text"
                placeholder="Search hotel or error…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Search hotel or error"
              />
              {searchQuery ? (
                <button type="button" className="cp-search-clear" onClick={() => setSearchQuery('')} aria-label="Clear search">
                  <X size={12} />
                </button>
              ) : null}
            </div>
            {/* display:contents on desktop; a scrolling chip row on phones */}
            <div className="ep-dlq-filter-row">
            <select className="cp-input" style={{ width: 150 }} aria-label="Status"
                    value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <select className="cp-input" style={{ width: 170 }} aria-label="Error category"
                    value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
              {CATEGORY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <input
              type="text"
              className="cp-input"
              style={{ width: 150 }}
              placeholder="Feed, e.g. audit"
              aria-label="Filter by feed"
              value={fileTypeFilter}
              onChange={(e) => setFileTypeFilter(e.target.value)}
            />
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12.5, color: 'var(--lodge-onyx-600)', whiteSpace: 'nowrap' }}>
              <input type="checkbox" checked={includeMissed} onChange={(e) => setIncludeMissed(e.target.checked)} />
              Include missed files
            </label>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12.5, color: 'var(--lodge-onyx-600)', whiteSpace: 'nowrap' }}>
              <input type="checkbox" checked={onlyWithErrors} onChange={(e) => setOnlyWithErrors(e.target.checked)} />
              Only rows with an issue or error
            </label>
            </div>
            <div className="cp-toolbar-spacer" />
            {activeFilterCount > 0 ? <span className="cp-meta">{activeFilterCount} filters</span> : null}
          </div>
        </div>

        {/* Rows */}
        <div className="flex-1 overflow-auto" onScroll={handleRowsScroll}>
          {loading && <EtlLoading>Loading the issue queue…</EtlLoading>}
          {!loading && error && (
            <div style={{ padding: 18 }}>
              <EtlBanner tone="error" title="Could not load the issue queue">{error}</EtlBanner>
            </div>
          )}
          {!loading && !error && filteredRows.length === 0 && (
            <ConfigEmptyState
              title={statusFilter === 'dlq' ? 'Nothing needs attention' : 'No matches'}
              body={statusFilter === 'dlq'
                ? 'Every file in scope processed cleanly.'
                : 'Clear the search, the status tile or the date range to widen the scope.'}
            />
          )}
          {!loading && !error && filteredRows.length > 0 && (
            <>
              {/* Render only the active responsive layout; mounting both made
                  large complete ranges pay twice the row-rendering cost. */}
              {isDesktop && (
                <table className="it-tbl">
                  <thead>
                    <tr>
                      <th>Hotel</th>
                      <th>Feed</th>
                      <th>Business date</th>
                      <th>Status</th>
                      <th>Issue</th>
                      <th>Last error</th>
                      <th>Tries</th>
                      <th>Updated</th>
                      <th aria-label="Open" style={{ width: 32 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.map((r) => (
                      <tr
                        key={r.id}
                        onClick={() => handleSelect(r.id)}
                        className={`cp-row it-row-hover${selectedId === r.id ? ' it-row-selected' : ''}`}
                        style={{ cursor: 'pointer' }}
                      >
                        <td className="cp-cell">
                          <div className="cp-name">{locationNameById.get(r.locationId) || `Hotel ${r.locationId}`}</div>
                          <div className="cp-code">{r.locationId}</div>
                        </td>
                        <td className="cp-cell"><span className="cp-mono">{r.fileType}</span></td>
                        <td className="cp-cell" title={String(r.effectiveDate || '')}>
                          <span className="cp-mono">{formatEffectiveDate(r.effectiveDate)}</span>
                        </td>
                        <td className="cp-cell"><StatusPill status={r.status} /></td>
                        <td className="cp-cell">
                          <span style={{ fontSize: 12.5, color: 'var(--lodge-onyx-600)' }}>{categoryLabel(r.lastErrorCategory)}</span>
                        </td>
                        <td className="cp-cell" style={{ maxWidth: 300 }} title={r.lastErrorMessage || ''}>
                          <div className="cp-code" style={{ color: r.lastErrorMessage ? 'var(--color-error-ink)' : undefined }}>
                            {r.lastErrorMessage || '—'}
                          </div>
                        </td>
                        <td className="cp-cell"><span className="cp-mono">{r.attempts}</span></td>
                        <td className="cp-cell" title={formatAbsolute(r.lastTransitionAt)}>
                          <span className="cp-code">{formatRelative(r.lastTransitionAt)}</span>
                        </td>
                        <td className="cp-cell-tight" style={{ color: 'var(--lodge-onyx-300)' }}>
                          <ChevronRight size={13} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* Mobile card list */}
              {!isDesktop && (
                <DlqCardList
                  rows={visibleRows}
                  selectedId={selectedId}
                  onSelect={handleSelect}
                  locationNameById={locationNameById}
                  categoryLabel={categoryLabel}
                  statusLabel={STATUS_LABEL}
                />
              )}
              {visibleRows.length < filteredRows.length && (
                <div className="py-2 text-center text-[11px] text-gray-400">
                  Scroll to load more rows · {visibleRows.length.toLocaleString()} of {filteredRows.length.toLocaleString()} shown
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Phones: bottom sheet with the error, key facts and quick actions */}
      {mobileSheetMode && selectedId ? (
        <DlqIssueSheet
          open={mobileSheetOpen}
          onOpenChange={(next) => { if (!next) closeMobileSheet(); }}
          row={rows.find((r) => r.id === selectedId)}
          detail={detail}
          detailLoading={detailLoading}
          locationName={(() => {
            const id = detail?.state?.locationId ?? rows.find((r) => r.id === selectedId)?.locationId;
            return id != null ? (locationNameById.get(id) || `Hotel ${id}`) : null;
          })()}
          categoryLabel={categoryLabel}
          statusLabel={STATUS_LABEL}
          actionNotes={actionNotes}
          onNotesChange={setActionNotes}
          actionFeedback={actionFeedback}
          actionInProgress={actionInProgress}
          onAction={(action) => performAction(action)}
          onViewImport={fromSheet(() => setImportReviewOpen(true))}
          onEditConfig={fromSheet(() => setFileConfigEditOpen(true))}
          onMoreDetails={fromSheet(() => setMobileFullDetail(true))}
        />
      ) : null}

      {/* Right: detail panel — full screen on mobile, side panel on desktop */}
      {showDetailPane && (
        <div className="fixed inset-0 z-40 md:static md:z-auto md:w-[420px] flex-shrink-0 bg-white md:rounded-2xl md:border md:border-gray-200 overflow-hidden flex flex-col">
          <div className="px-3 pb-3 pt-[max(0.75rem,env(safe-area-inset-top))] md:p-4 border-b border-gray-100 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <button
                onClick={() => { setSelectedId(null); setDetail(null); }}
                className="md:hidden p-1.5 hover:bg-gray-100 rounded-full text-gray-500"
              >
                <ChevronRight size={18} className="rotate-180" />
              </button>
              <FileText size={16} className="text-gray-500 hidden md:block" />
              <h3 className="text-[14px] font-semibold text-gray-800 truncate">
                {detail
                  ? `${detail.state.fileType} — ${locationNameById.get(detail.state.locationId) || `Hotel ${detail.state.locationId}`}`
                  : `Record #${selectedId}`}
              </h3>
              <span className="hidden sm:inline text-[11px] text-gray-400">#{selectedId}</span>
            </div>
            <div className="flex items-center gap-1">
              {detail && (
                <button
                  onClick={() => setFileConfigEditOpen(true)}
                  title="Edit file matching config for this hotel / file type"
                  className="flex items-center gap-1 p-1.5 sm:px-2 sm:py-1 text-[11px] font-medium text-gray-500 hover:text-blue-600 hover:bg-blue-50 border border-gray-200 hover:border-blue-200 rounded transition-colors"
                >
                  <Settings size={12} />
                  <span className="hidden sm:inline">Edit Config</span>
                </button>
              )}
              <button
                onClick={() => { setSelectedId(null); setDetail(null); }}
                className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600 hidden md:block"
              >
                <X size={16} />
              </button>
            </div>
          </div>
          {/* Orange accent bar on mobile */}
          <div className="h-1 bg-orange-500 md:hidden" />

          <div className="flex-1 overflow-auto">
            {detailLoading && (
              <div className="p-6 text-center text-gray-400 text-[13px]">Loading…</div>
            )}
            {!detailLoading && detail && (
              <>
                <div className="p-4 border-b border-gray-100">
                  <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2 md:hidden">Details</div>
                  <div className="grid grid-cols-2 gap-y-2 text-[12px]">
                    <div className="text-gray-500">Location</div>
                    <div className="text-gray-800 font-medium text-right md:text-left">{locationNameById.get(detail.state.locationId) || `Hotel ${detail.state.locationId}`}</div>
                    {role === 'engineer' && (
                      <>
                        <div className="text-gray-500">Location ID</div>
                        <div className="text-gray-800">{detail.state.locationId}</div>
                      </>
                    )}
                    <div className="text-gray-500">File Type</div>
                    <div className="text-gray-800 font-medium">{detail.state.fileType}</div>
                    <div className="text-gray-500">Status</div>
                    <div><StatusPill status={detail.state.status} /></div>
                    <div className="text-gray-500">Attempts</div>
                    <div className="text-gray-800">{detail.state.attempts}</div>
                    <div className="text-gray-500">Issue</div>
                    <div className="text-gray-800">{categoryLabel(detail.state.lastErrorCategory)}</div>
                    {role === 'engineer' && (
                      <>
                        <div className="text-gray-500">OCR Service</div>
                        <div className="text-gray-800">{detail.state.ocrService || '—'}</div>
                      </>
                    )}
                    <div className="text-gray-500">Received</div>
                    <div className="text-gray-800" title={formatAbsolute(detail.state.receivedAt)}>{formatLocalWithRelative(detail.state.receivedAt)}</div>
                    <div className="text-gray-500">Last Update</div>
                    <div className="text-gray-800" title={formatAbsolute(detail.state.lastTransitionAt)}>{formatLocalWithRelative(detail.state.lastTransitionAt)}</div>
                  </div>
                  {detail.state.lastErrorMessage && (
                    <div className="mt-3 p-2 bg-red-50 border border-red-100 rounded text-[11px] text-red-800 break-words">
                      {detail.state.lastErrorMessage}
                    </div>
                  )}
                  {role === 'engineer' && detail.state.sourceBlobPath && (
                    <div className="mt-2 text-[11px] text-gray-500 break-all">
                      <span className="text-gray-400">source:</span> {detail.state.sourceBlobPath}
                    </div>
                  )}
                  {role === 'engineer' && detail.state.archivedBlobPath && (
                    <div className="mt-1 text-[11px] text-gray-500 break-all">
                      <span className="text-gray-400">archive:</span> {detail.state.archivedBlobPath}
                    </div>
                  )}
                  {detail.state.archivedBlobPath && (
                    <div className="mt-2">
                      <button
                        onClick={async () => {
                          if (archiveOpen) { setArchiveOpen(false); return; }
                          setArchiveOpen(true);
                          if (!archiveFiles) {
                            setArchiveLoading(true);
                            try {
                              const result = await getEtlDlqArchiveFiles(detail.state.id);
                              setArchiveFiles(result);
                            } catch (_) {
                              setArchiveFiles({ files: [] });
                            } finally {
                              setArchiveLoading(false);
                            }
                          }
                        }}
                        className="text-[11px] text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                      >
                        <FileText size={11} />
                        {archiveOpen ? 'Hide' : 'Browse'} Archive Files
                        {archiveLoading && <span className="text-gray-400 ml-1">(loading...)</span>}
                      </button>
                      {archiveOpen && archiveFiles && (
                        <div className="mt-1.5">
                          {archiveFiles.files.length === 0 ? (
                            <div className="p-2 text-[11px] text-gray-400 border border-gray-200 rounded-lg">No archive files found</div>
                          ) : (
                            <>
                              <div className="border border-gray-200 rounded-lg overflow-hidden">
                                {archiveFiles.files.map((file, idx) => {
                                  const isSelected = archiveSelectedFile?.path === file.path;
                                  return (
                                    <button
                                      key={idx}
                                      onClick={() => setArchiveSelectedFile(isSelected ? null : file)}
                                      className={`w-full flex items-center justify-between px-2.5 py-1.5 text-[11px] border-b border-gray-100 last:border-b-0 text-left ${
                                        isSelected ? 'bg-blue-100 text-blue-800' : file.isSource ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'
                                      }`}
                                    >
                                      <span className="flex items-center gap-1.5 min-w-0">
                                        <FileText size={11} className={isSelected ? 'text-blue-600' : file.isSource ? 'text-blue-500' : 'text-gray-400'} />
                                        <span className="truncate">{file.name}</span>
                                        {file.isSource && <span className="text-[9px] text-blue-400 whitespace-nowrap">(source)</span>}
                                      </span>
                                      <span className="text-[10px] text-gray-400 ml-2 whitespace-nowrap">
                                        {file.size ? `${(file.size / 1024).toFixed(1)}KB` : ''}
                                      </span>
                                    </button>
                                  );
                                })}
                              </div>
                              {archiveSelectedFile && (
                                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
                                  onClick={(e) => { if (e.target === e.currentTarget) setArchiveSelectedFile(null); }}>
                                  <div className="bg-white rounded-xl shadow-2xl w-[90vw] h-[85vh] flex flex-col overflow-hidden">
                                    <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 bg-gray-50">
                                      <div className="flex items-center gap-2 min-w-0">
                                        <FileText size={14} className="text-blue-500 flex-shrink-0" />
                                        <span className="text-[13px] font-medium text-gray-800 truncate">{archiveSelectedFile.name}</span>
                                        {archiveSelectedFile.isSource && <span className="text-[10px] text-blue-500 bg-blue-50 px-1.5 py-0.5 rounded">source</span>}
                                        <span className="text-[11px] text-gray-400">{archiveSelectedFile.size ? `${(archiveSelectedFile.size / 1024).toFixed(1)}KB` : ''}</span>
                                      </div>
                                      <button onClick={() => setArchiveSelectedFile(null)}
                                        className="p-1 hover:bg-gray-200 rounded transition-colors">
                                        <X size={16} className="text-gray-500" />
                                      </button>
                                    </div>
                                    <div className="flex-1 overflow-hidden">
                                      <FileViewerPanel
                                        blobUrl={archiveSelectedFile.url}
                                        fileType={detectFileViewerType(archiveSelectedFile.name, 'text')}
                                        className="h-full"
                                      />
                                    </div>
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {(detail.state.importId || detail.state.archivedBlobPath || detail.state.imports_nightAuditPath || detail.state.sourceBlobPath) && (
                    <button
                      onClick={() => setImportReviewOpen(true)}
                      className="mt-3 w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white text-[12px] font-medium rounded-lg transition-colors"
                    >
                      <Eye size={14} />
                      {detail.state.importId ? 'View Import Data' : 'View Source File'}
                    </button>
                  )}
                </div>

                {/* Event timeline */}
                <div className="p-4 border-b border-gray-100">
                  <div className="text-[12px] font-semibold text-gray-500 mb-3 flex items-center gap-1.5 uppercase tracking-wider md:normal-case md:tracking-normal md:font-medium">
                    <Clock size={12} />
                    Event Timeline
                  </div>
                  <div className="space-y-3 md:space-y-2">
                    {(detail.events || []).map((ev, idx) => (
                      <div key={ev.id} className="text-[11px] flex gap-3 md:block">
                        {/* Timeline dot + line on mobile */}
                        <div className="flex flex-col items-center md:hidden flex-shrink-0">
                          <div className={`w-2.5 h-2.5 rounded-full mt-0.5 ${idx === 0 ? 'bg-green-500' : 'bg-gray-300'}`} />
                          {idx < (detail.events || []).length - 1 && (
                            <div className="w-px flex-1 bg-gray-200 mt-1" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0 pb-1">
                          <div className="text-gray-400 text-[10px] md:hidden mb-0.5">{formatRelative(ev.occurredAt)}</div>
                          <div className="flex items-center gap-2 text-gray-700 flex-wrap">
                            <span className="text-gray-400 hidden md:inline">{formatRelative(ev.occurredAt)}</span>
                            <span className="text-gray-300 hidden md:inline">·</span>
                            <span className="font-medium">{ev.stage}</span>
                            <span className="text-gray-400">
                              — {STATUS_LABEL[ev.fromStatus] || ev.fromStatus || '—'} → {STATUS_LABEL[ev.toStatus] || ev.toStatus}
                            </span>
                          </div>
                          {ev.message && (
                            <div className="mt-0.5 text-gray-500 break-words">{ev.message}</div>
                          )}
                        </div>
                      </div>
                    ))}
                    {(!detail.events || detail.events.length === 0) && (
                      <div className="text-[11px] text-gray-400">No events recorded.</div>
                    )}
                  </div>
                </div>

                {/* DLQ action audit trail */}
                {detail.actions && detail.actions.length > 0 && (
                  <div className="p-4 border-b border-gray-100">
                    <div className="text-[12px] font-medium text-gray-500 mb-2">Operator Actions</div>
                    <div className="space-y-2">
                      {detail.actions.map((act) => (
                        <div key={act.id} className="text-[11px]">
                          <div className="flex items-center gap-2 text-gray-700">
                            <span className="text-gray-400">{formatRelative(act.performedAt)}</span>
                            <span className="text-gray-300">·</span>
                            <span className="font-medium">{act.action}</span>
                            <span className="text-gray-400">by {act.performedBy} ({act.performedByRole})</span>
                          </div>
                          {act.notes && (
                            <div className="ml-2 mt-0.5 text-gray-500 break-words">{act.notes}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="p-4">
                  <div className="text-[12px] font-medium text-gray-500 mb-2">Actions</div>
                  <textarea
                    value={actionNotes}
                    onChange={(e) => setActionNotes(e.target.value)}
                    placeholder="Note (required for resolve / escalate)"
                    className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-[12px] text-gray-700 mb-2 resize-none"
                    rows={2}
                  />
                  {actionFeedback && (
                    <div className={`mb-2 p-2 rounded text-[11px] ${actionFeedback.tone === 'error' ? 'bg-red-50 text-red-700 border border-red-100' : 'bg-emerald-50 text-emerald-700 border border-emerald-100'}`}>
                      {actionFeedback.message}
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => performAction('replay')}
                      disabled={actionInProgress}
                      className="flex items-center justify-center gap-1.5 px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white text-[12px] font-medium rounded-lg disabled:opacity-50"
                    >
                      <PlayCircle size={14} />
                      Replay
                    </button>
                    <button
                      onClick={() => performAction('retry_ocr')}
                      disabled={actionInProgress}
                      title="Retry OCR — restores from archive if working/ has been cleaned up"
                      className="flex items-center justify-center gap-1.5 px-3 py-2 bg-orange-100 hover:bg-orange-200 text-orange-800 text-[12px] font-medium rounded-lg border border-orange-200 disabled:opacity-50"
                    >
                      <RotateCcw size={14} />
                      Retry OCR
                    </button>
                    <button
                      onClick={() => performAction('mark_resolved')}
                      disabled={actionInProgress}
                      className="flex items-center justify-center gap-1.5 px-3 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-[12px] font-medium rounded-lg disabled:opacity-50"
                    >
                      <CheckCircle2 size={14} />
                      Mark Resolved
                    </button>
                    <button
                      onClick={() => performAction('escalate')}
                      disabled={actionInProgress}
                      className="flex items-center justify-center gap-1.5 px-3 py-2 bg-yellow-100 hover:bg-yellow-200 text-yellow-800 text-[12px] font-medium rounded-lg border border-yellow-200 disabled:opacity-50"
                    >
                      <Flag size={14} />
                      Escalate
                    </button>
                    <button
                      onClick={() => performAction('undo_dedupe')}
                      disabled={actionInProgress || !detail?.state?.messageId}
                      title={detail?.state?.messageId ? 'Clear etlProcessedMessage rows for this messageId so the email can be reprocessed' : 'No messageId on this state row — undo dedupe is unavailable'}
                      className="flex items-center justify-center gap-1.5 px-3 py-2 bg-purple-100 hover:bg-purple-200 text-purple-800 text-[12px] font-medium rounded-lg border border-purple-200 disabled:opacity-50"
                    >
                      <Trash2 size={14} />
                      Undo Dedupe
                    </button>
                    <button
                      onClick={async () => {
                        const opening = !aiReplayOpen;
                        setAiReplayOpen(opening);
                        if (opening && !aiPrompt && detail?.state) {
                          setAiPromptLoading(true);
                          try {
                            const result = await getEtlDlqGeneratePrompt(
                              detail.state.locationId, detail.state.fileType
                            );
                            setAiPrompt(result.generatedPrompt || '');
                          } catch (e) {
                            setAiPrompt('');
                          } finally {
                            setAiPromptLoading(false);
                          }
                        }
                      }}
                      disabled={actionInProgress}
                      className="flex items-center justify-center gap-1.5 px-3 py-2 bg-purple-500 hover:bg-purple-600 text-white text-[12px] font-medium rounded-lg disabled:opacity-50 col-span-2"
                    >
                      <Sparkles size={14} />
                      Replay with AI
                    </button>
                    {aiReplayOpen && (
                      <div className="col-span-2 p-3 bg-purple-50 rounded-lg border border-purple-200 space-y-3">
                        <div>
                          <label className="block text-[11px] font-medium text-purple-700 mb-1">AI Model</label>
                          <AiModelPicker value={aiModel} onChange={setAiModel} compact />
                        </div>
                        <div>
                          <label className="block text-[11px] font-medium text-purple-700 mb-1">Data Types</label>
                          <div className="flex gap-2 flex-wrap">
                            {['revenue', 'roomStats', 'occupancy', 'journal'].map((dt) => (
                              <label key={dt} className="flex items-center gap-1 text-[11px] text-gray-700">
                                <input
                                  type="checkbox"
                                  checked={aiDataTypes.includes(dt)}
                                  onChange={(e) => {
                                    setAiDataTypes(prev =>
                                      e.target.checked ? [...prev, dt] : prev.filter(d => d !== dt)
                                    );
                                  }}
                                  className="rounded border-gray-300"
                                />
                                {dt}
                              </label>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className="block text-[11px] font-medium text-purple-700 mb-1">
                            AI Extraction Prompt {aiPromptLoading && <span className="text-purple-400">(loading...)</span>}
                          </label>
                          <textarea
                            value={aiPrompt}
                            onChange={(e) => setAiPrompt(e.target.value)}
                            placeholder={aiPromptLoading ? 'Generating prompt from schema config...' : 'Describe what data to extract from this file...'}
                            rows={5}
                            className="w-full px-2 py-1.5 border border-purple-200 rounded text-[11px] text-gray-700 font-mono resize-y"
                          />
                          <div className="text-[10px] text-purple-500 mt-0.5">
                            Auto-generated from schema config. Edit to refine extraction instructions.
                          </div>
                        </div>
                        <div>
                          <label className="block text-[11px] font-medium text-purple-700 mb-1">
                            Additional context (optional)
                          </label>
                          <textarea
                            value={aiContext}
                            onChange={(e) => setAiContext(e.target.value)}
                            placeholder="e.g. OCR missed revenue rows between ROOM OTHER and PAYMENTS sections"
                            rows={2}
                            className="w-full px-2 py-1.5 border border-purple-200 rounded text-[11px] text-gray-700 resize-none"
                          />
                        </div>
                        <button
                          onClick={() => {
                            const fullPrompt = [aiPrompt.trim(), aiContext.trim()].filter(Boolean).join('\n\nAdditional context: ');
                            performAction('replay_with_ai', {
                              preferredModel: aiModel,
                              dataTypes: aiDataTypes,
                              aiContext: fullPrompt || undefined,
                            });
                            setAiReplayOpen(false);
                          }}
                          disabled={actionInProgress || aiDataTypes.length === 0}
                          className="w-full px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white text-[12px] font-medium rounded-lg disabled:opacity-50"
                        >
                          Run AI Extraction
                        </button>
                      </div>
                    )}
                    <div className="flex gap-1 col-span-2">
                      <input
                        type="text"
                        value={reassignType}
                        onChange={(e) => setReassignType(e.target.value)}
                        placeholder="new fileType (for reassign)"
                        className="flex-1 px-2 py-2 border border-gray-200 rounded-lg text-[12px] text-gray-700 min-w-0"
                      />
                      <button
                        onClick={() => performAction('reassign_type', { newFileType: reassignType })}
                        disabled={actionInProgress || !reassignType.trim()}
                        className="flex items-center gap-1 px-3 py-2 bg-blue-100 hover:bg-blue-200 text-blue-800 text-[12px] font-medium rounded-lg border border-blue-200 disabled:opacity-50"
                      >
                        <ArrowRightLeft size={14} />
                        Reassign
                      </button>
                    </div>
                  </div>

                  {/* Convert to AI panel — shows when an AI replay has been attempted */}
                  {detail?.actions?.some(a => a.action === 'replay_with_ai') && (
                    <div className="mt-3 p-3 bg-purple-50 rounded-lg border-l-4 border-l-purple-400 border border-purple-200">
                      <div className="text-[12px] font-medium text-purple-700 mb-2">
                        {detail?.state?.status === 'normalized'
                          ? <>AI extraction succeeded. Convert this hotel's <span className="font-bold">{detail?.state?.fileType}</span> permanently?</>
                          : <>AI replay attempted. Convert this hotel's <span className="font-bold">{detail?.state?.fileType}</span> to AI extraction?</>
                        }
                      </div>
                      <button
                        onClick={() => {
                          // Extract model, dataTypes, and prompt from the last AI replay event
                          const lastAiEvent = [...(detail?.events || [])].reverse().find(
                            e => e.message && e.message.includes('AI Replay requested')
                          );
                          let model = aiModel;
                          let types = aiDataTypes;
                          let prompt = aiPrompt;
                          if (lastAiEvent?.metadata) {
                            try {
                              const meta = typeof lastAiEvent.metadata === 'string'
                                ? JSON.parse(lastAiEvent.metadata) : lastAiEvent.metadata;
                              if (meta.preferredModel) model = meta.preferredModel;
                              if (meta.dataTypes) types = meta.dataTypes;
                              if (meta.aiContext) prompt = meta.aiContext;
                            } catch (_) {}
                          }
                          performAction('convert_to_ai', {
                            preferredModel: model,
                            dataTypes: types,
                            userPrompt: prompt || undefined,
                          });
                        }}
                        disabled={actionInProgress}
                        className="px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white text-[12px] font-medium rounded-lg disabled:opacity-50"
                      >
                        Convert to AI Extraction
                      </button>
                    </div>
                  )}

                  {/* Engineering-only actions */}
                  {role === 'engineer' && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <div className="text-[11px] font-medium text-gray-500 mb-1.5">Engineering</div>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => performAction('replay_from_stage', { fromStage: 'received' })}
                          disabled={actionInProgress}
                          className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-[11px] font-medium rounded-lg border border-gray-200 disabled:opacity-50"
                        >
                          Replay from start
                        </button>
                        <button
                          onClick={() => performAction('replay_from_stage', { fromStage: 'normalizer' })}
                          disabled={actionInProgress}
                          className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-[11px] font-medium rounded-lg border border-gray-200 disabled:opacity-50"
                        >
                          Replay from normalizer
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Import Review modal */}
      {importReviewOpen && detail && (
        <ImportReviewModal
          detail={detail}
          locationName={locationNameById.get(detail.state.locationId) || `Hotel ${detail.state.locationId}`}
          onClose={() => setImportReviewOpen(false)}
        />
      )}

      {/* Feed options — the same dialog the tracking views open, so the
          recognition rules and the alert schedule are never edited apart. */}
      {fileConfigEditOpen && detail && (
        <FeedOptionsModal
          config={{
            hotelId: detail.state.locationId,
            fileType: detail.state.fileType,
            displayName: detail.state.fileType,
            locationName: locationNameById.get(detail.state.locationId) || `Hotel ${detail.state.locationId}`,
          }}
          currentOrganization={currentOrganization}
          onSave={() => setFileConfigEditOpen(false)}
          onCancel={() => setFileConfigEditOpen(false)}
        />
      )}

      {/* Reprocess Hotel Day modal — wraps the existing emailReprocess HTTP
          function in a CS-friendly form. Clears the dedupe table for the hotel
          + date range first, then triggers emailReprocess. */}
      {reprocessOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => !reprocessInProgress && setReprocessOpen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-[480px] max-w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Calendar size={18} className="text-blue-500" />
                <h3 className="text-[15px] font-semibold text-gray-800">Reprocess Hotel Day</h3>
              </div>
              <button
                onClick={() => !reprocessInProgress && setReprocessOpen(false)}
                disabled={reprocessInProgress}
                className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600 disabled:opacity-50"
              >
                <X size={16} />
              </button>
            </div>
            <p className="text-[12px] text-gray-500 mb-4">
              Clears the dedupe table for the hotel and date range, then re-pulls every email
              for that hotel from Gmail (inbox + All Mail). Use this when files for a specific
              day didn't process correctly and you want to start over.
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-[11px] font-medium text-gray-600 mb-1">Hotel ID</label>
                <input
                  type="text"
                  value={reprocessHotelId}
                  onChange={(e) => setReprocessHotelId(e.target.value)}
                  placeholder="e.g. 17"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-[13px]"
                  disabled={reprocessInProgress}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-medium text-gray-600 mb-1">Start Date *</label>
                  <input
                    type="date"
                    value={reprocessStartDate}
                    onChange={(e) => setReprocessStartDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-[13px]"
                    disabled={reprocessInProgress}
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-600 mb-1">End Date</label>
                  <input
                    type="date"
                    value={reprocessEndDate}
                    onChange={(e) => setReprocessEndDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-[13px]"
                    disabled={reprocessInProgress}
                  />
                </div>
              </div>
              <div className="flex items-center gap-4 text-[12px]">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={reprocessIncludeArchive}
                    onChange={(e) => setReprocessIncludeArchive(e.target.checked)}
                    disabled={reprocessInProgress}
                  />
                  Include Gmail archive (recommended)
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={reprocessDryRun}
                    onChange={(e) => setReprocessDryRun(e.target.checked)}
                    disabled={reprocessInProgress}
                  />
                  Dry run
                </label>
              </div>
              {reprocessFeedback && (
                <div className={`p-2 rounded text-[12px] ${reprocessFeedback.tone === 'error' ? 'bg-red-50 text-red-700 border border-red-100' : 'bg-emerald-50 text-emerald-700 border border-emerald-100'}`}>
                  {reprocessFeedback.message}
                </div>
              )}
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => !reprocessInProgress && setReprocessOpen(false)}
                  disabled={reprocessInProgress}
                  className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-[13px] font-medium rounded-lg disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={submitReprocessHotelDay}
                  disabled={reprocessInProgress || !reprocessHotelId.trim() || !reprocessStartDate.trim()}
                  className="flex-1 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-[13px] font-medium rounded-lg disabled:opacity-50"
                >
                  {reprocessInProgress ? 'Reprocessing…' : 'Reprocess'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
